/**
 * Stripe v15 / API 2026-04-22.dahlia compatibility helpers
 *
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 *
 * ---------------------------------------------------------------------------
 * Why this module exists
 * ---------------------------------------------------------------------------
 *
 * Under Stripe API 2026-03-25.dahlia and onward, several invoice and line-item
 * fields moved or were renamed:
 *
 *   • Invoice.payment_intent             →  invoice.payments.data[0].payment.payment_intent
 *   • InvoiceLineItem.price              →  line.pricing.price_details (productId/priceId)
 *                                            +  line.parent.type      (one_time / recurring)
 *                                            +  line.parent.subscription_item_details.plan.interval
 *   • Invoice.total_tax_amounts          →  invoice.total_taxes
 *
 * These helpers provide a single fallback chain that works on BOTH API
 * generations: the new path is consulted only if the legacy path is absent.
 * The same code therefore runs correctly whether the backend is pinned to
 * the legacy API or to API 2026, and existing test fixtures using the
 * legacy shape continue to pass.
 *
 * FCR S291 DD-3: Extracted from thanksAdapter.ts to a single source of truth
 * so every adapter that reads Stripe invoice lines (thanksAdapter,
 * subscription-adapter, invoicesAdapter) shares one copy. Future API revisions
 * are then a single-file change.
 *
 * Generic typing: every helper accepts ``unknown``-shaped inputs internally
 * and uses optional-chaining to read both shapes. Callers pass whatever
 * line/invoice type they have — Order, OrderData["invoice"], a raw
 * StripeObject — and the helpers return the same result. No runtime
 * dependency on a specific TypeScript shape.
 */

// ---------------------------------------------------------------------------
// Internal shape types (loose-typed for cross-adapter reuse)
// ---------------------------------------------------------------------------

interface InvoiceShape {
  payment_intent?: string | null;
  payments?: {
    data?: Array<{ payment?: { payment_intent?: string | null } }>;
  };
  total_tax_amounts?: Array<{ amount?: number }>;
  total_taxes?: Array<{ amount?: number }>;
}

interface LineItemShape {
  /**
   * M-A (S652): Stripe's own allocation of the order-level discount onto this
   * line. Every discount in this shop reaches Stripe at the order level (a
   * percent_off coupon on the session, for both the volume discount and a
   * manual coupon), so Stripe spreads it across the lines and what lands here
   * IS what the customer paid for this line. Typed loosely because the two
   * adapters that read it declare the surrounding invoice with different
   * generated shapes.
   */
  discount_amounts?: unknown;
  price?: {
    id?: string;
    product?: string;
    type?: string;
    recurring?: { interval?: string } | null;
  } | null;
  pricing?: {
    type?: string;
    price_details?: {
      product?: string;
      // price may be a bare string ID or a fully expanded Stripe Price object
      // (when lines.data.pricing.price_details.price is included in the expand list)
      price?:
        | string
        | {
            id?: string;
            recurring?: { interval?: string } | null;
            type?: string;
          }
        | null;
    };
  };
  parent?: {
    type?: string;
    subscription_item_details?: {
      // Legacy API: plan.interval used for billing-interval detection.
      plan?: { interval?: string } | null;
      // API 2026 fields present on Stripe SDK 15 line item objects.
      // Expanding these avoids TypeScript's "weak type" rejection when the
      // actual Stripe object carries no `plan` but has these fields instead.
      invoice_item?: string | null;
      proration?: boolean;
      proration_details?: unknown;
      subscription?: string | null;
      subscription_item?: string | null;
    } | null;
  } | null;
}

// ---------------------------------------------------------------------------
// Public helpers
// ---------------------------------------------------------------------------

/**
 * Return the payment_intent ID for an invoice across both API generations.
 *
 * Legacy: invoice.payment_intent
 * API 2026: invoice.payments.data[0].payment.payment_intent
 *
 * @returns The payment_intent ID, or null if neither path resolves it.
 */
export function getInvoicePaymentIntent(
  invoice: InvoiceShape | null | undefined,
): string | null {
  if (!invoice) return null;
  const legacy = invoice.payment_intent;
  if (legacy) return legacy;
  const modern = invoice.payments?.data?.[0]?.payment?.payment_intent;
  return modern ?? null;
}

/**
 * Return the total tax amount in cents for an invoice across both API generations.
 *
 * Legacy: invoice.total_tax_amounts[]
 * API 2026: invoice.total_taxes[]  (renamed in Stripe's Basil release)
 *
 * Every entry is summed. Stripe records one entry per tax rate, so an order
 * taxed under more than one rate carries more than one entry, and reading only
 * the first reported less tax than the customer paid on the order summary.
 * getLineTaxAmount() below and _line_tax_cents_for_line() on the server both
 * sum every entry, and this one must agree with them.
 *
 * @returns Tax amount in cents (0 if absent).
 */
export function getInvoiceTaxAmount(
  invoice: InvoiceShape | null | undefined,
): number {
  if (!invoice) return 0;
  const entries = invoice.total_tax_amounts?.length
    ? invoice.total_tax_amounts
    : (invoice.total_taxes ?? []);
  return entries.reduce(
    (sum, entry) =>
      sum + (typeof entry?.amount === "number" && entry.amount > 0 ? entry.amount : 0),
    0,
  );
}

/**
 * Return the Stripe product ID for an invoice line item across both API generations.
 *
 * Legacy: line.price.product
 * API 2026: line.pricing.price_details.product
 *
 * @returns The product ID, or undefined if neither path resolves it.
 */
export function getLineProductId(
  item: LineItemShape | null | undefined,
): string | undefined {
  if (!item) return undefined;
  return (
    item.price?.product ??
    item.pricing?.price_details?.product ??
    undefined
  );
}

/**
 * Return the Stripe price ID for an invoice line item across both API generations.
 *
 * Legacy (pre-2025-03-31.basil): line.price.id
 * API 2026 (2025-03-31.basil+): line.pricing.price_details.price
 *
 * @returns The price ID string, or undefined if neither path resolves it.
 */
export function getLinePriceId(
  item: LineItemShape | null | undefined,
): string | undefined {
  if (!item) return undefined;
  // API 2026: pricing.price_details.price may be an expanded Price object or a bare string ID
  if (item.pricing?.type === "price_details") {
    const p = item.pricing.price_details?.price;
    if (typeof p === "string") return p;
    if (typeof p === "object" && p && "id" in p) return (p as { id?: string }).id ?? undefined;
  }
  // Legacy fallback
  return item.price?.id ?? undefined;
}

/**
 * Return the line item billing type ("recurring" | "one_time") across both API generations.
 *
 * H-1 (FCR S557). This helper was the one of four in this file that was never
 * brought forward when Stripe changed the shape of its data. It read only
 * line.price.type and line.parent.type, both of which are absent on the API
 * generation this application targets, and returned undefined.
 *
 * The consequence was not a visible error. A view that keeps only lines it can
 * confirm are one-time purchases, and a view that keeps only lines it can
 * confirm are subscriptions, both discard a line that answers neither question
 * -- silently. That is how a paid purchase disappeared from the customer's
 * account entirely (SU-28).
 *
 * Resolution order, matching the three sibling helpers:
 *   1. The expanded price object at pricing.price_details.price -- the field the
 *      server already requests and nothing read for type.
 *   2. The recurring interval. A line with an interval is a subscription; a line
 *      without one is a one-time purchase.
 *   3. The legacy line.price.type and line.parent.type fields.
 *
 * @returns "recurring" | "one_time", or undefined only when the line carries no
 *          usable pricing data at all.
 */
export function getLineType(
  item: LineItemShape | null | undefined,
): "recurring" | "one_time" | undefined {
  if (!item) return undefined;

  // 1. The expanded Price object, read first exactly as the sibling helpers do.
  if (item.pricing?.type === "price_details") {
    const price = item.pricing.price_details?.price;
    if (typeof price === "object" && price) {
      const priceType = price.type;
      if (priceType === "recurring" || priceType === "one_time") {
        return priceType;
      }
      // An expanded Price carries `recurring` only when it is a subscription
      // price. Its presence is as reliable as the type field.
      if ("recurring" in price) {
        return price.recurring ? "recurring" : "one_time";
      }
    }
  }

  // 2. The billing interval. getLineInterval already reads every generation.
  const interval = getLineInterval(item);
  if (interval) return "recurring";

  // 3. Legacy fields, kept so records fetched before the API change still resolve.
  const fromPrice = item.price?.type;
  if (fromPrice === "recurring" || fromPrice === "one_time") {
    return fromPrice;
  }
  if (item.parent?.type === "subscription_item_details") return "recurring";
  if (item.parent?.type === "invoice_item_details") return "one_time";

  // A line with a price but no interval anywhere is a one-time purchase. This is
  // the case that previously returned undefined and dropped the product.
  if (getLinePriceId(item)) return "one_time";

  return undefined;
}

/**
 * Return the recurring interval ("month" | "year" | etc.) across both API generations.
 *
 * Legacy (pre-2025-03-31.basil): line.price.recurring.interval
 * API 2026 (2025-03-31.basil+): line.pricing.price_details.price.recurring.interval
 *   (requires expand=['lines.data.pricing.price_details.price'] on the invoice fetch)
 *
 * Both line.price and line.parent.subscription_item_details.plan were removed
 * in Stripe API 2025-03-31.basil. Only the pricing.price_details path is valid
 * in API 2026-04-22.dahlia and later.
 *
 * @returns The interval string, or undefined.
 */
export function getLineInterval(
  item: LineItemShape | null | undefined,
): string | undefined {
  if (!item) return undefined;
  // API 2026: read from the expanded Price object inside pricing.price_details
  if (item.pricing?.type === "price_details") {
    const p = item.pricing.price_details?.price;
    if (typeof p === "object" && p && "recurring" in p) {
      const interval = (p as { recurring?: { interval?: string } }).recurring?.interval;
      if (interval) return interval;
    }
  }
  // Legacy fallback (pre-2025-03-31.basil)
  const fromPrice = item.price?.recurring?.interval;
  if (fromPrice) return fromPrice;
  return item.parent?.subscription_item_details?.plan?.interval;
}

// ---------------------------------------------------------------------------
// M-A (S652): one answer to "what was taken off this line"
// ---------------------------------------------------------------------------
//
// The Thank You page and the billing-history invoice used to answer this
// question with two different sources, and after GP-16 one of those sources
// stopped describing anything that happened.
//
// GP-16 changed how the discount reaches Stripe. It stopped being a fixed
// per-product amount and became ONE blended percentage on the checkout session.
// Stripe applies that percentage to every line, so `line.discount_amounts` is
// the factual record of the reduction applied to this line. The shop's own
// `discount_breakdown` metadata records what each product EARNED from the
// volume tiers -- a different question, and not the one a price on a receipt is
// asking. Decision D (S652) sharpens this further: an order where a manual
// coupon beat the volume discount now writes NO breakdown at all, so any reader
// that priced from the metadata alone would price a coupon order at zero
// discount while Stripe charged the coupon rate.
//
// Both adapters call this so the two screens cannot drift apart again. It is
// the browser half of `_line_amount_for_product` in
// backend/apps/refunds/views.py, which applies the same ordering server-side.

/** How the effective discount on a line was arrived at. */
export type LineDiscountSource = "stripe" | "metadata" | "none";

export interface ResolvedLineDiscount {
  /**
   * The reduction actually applied to this line, in cents of the integration
   * currency. This is the money figure -- Stripe's allocation when there is
   * one, the metadata breakdown when there is not, and zero otherwise.
   */
  amount: number;
  /**
   * A percentage that describes `amount` against this line's gross. Derived
   * from the allocation when Stripe is the source (the blended rate the
   * customer was actually charged), and taken from the breakdown when the
   * metadata is. Zero when neither applies, or when the gross is unknown.
   */
  pct: number;
  /** Which record produced `amount`. */
  source: LineDiscountSource;
  /** What the shop's breakdown records this product EARNED, in cents. */
  metadataAmount: number;
  /** The tier percentage the breakdown records for this product. */
  metadataPct: number;
}

/**
 * Sum Stripe's discount allocations for one invoice line.
 *
 * Stripe may allocate more than one discount onto a line (a coupon and a
 * promotion, say), so every entry is summed rather than the first one taken.
 *
 * @returns Total cents removed from this line by Stripe, or 0.
 */
export function getLineStripeDiscountAmount(
  line: LineItemShape | null | undefined,
): number {
  const allocations = line?.discount_amounts;
  if (!Array.isArray(allocations)) return 0;
  return allocations.reduce((sum: number, entry: unknown) => {
    const amount = (entry as { amount?: unknown } | null)?.amount;
    return sum + (typeof amount === "number" && Number.isFinite(amount) ? amount : 0);
  }, 0);
}

/**
 * Resolve the discount to price one invoice line by: Stripe first, the shop's
 * breakdown metadata second, zero last.
 *
 * @param line          The Stripe invoice line.
 * @param breakdown     Parsed `discount_breakdown` metadata, or null when the
 *                      order carries none (a coupon order, after Decision D).
 * @param productId     Stripe product ID used to look the line up in the
 *                      breakdown.
 * @param grossCents    This line's pre-discount amount, used only to express
 *                      Stripe's allocation as a percentage. Pass 0 when it is
 *                      not known and `pct` is reported as 0 rather than guessed.
 */
export function resolveLineDiscount(
  line: LineItemShape | null | undefined,
  breakdown: Record<string, { amount: number; pct: number }> | null | undefined,
  productId: string | undefined,
  grossCents: number,
): ResolvedLineDiscount {
  const entry =
    breakdown && productId ? breakdown[productId] || null : null;
  const metadataAmount =
    entry && typeof entry.amount === "number" && entry.amount > 0 ? entry.amount : 0;
  const metadataPct =
    entry && typeof entry.pct === "number" && entry.pct > 0 ? entry.pct : 0;

  const stripeAmount = getLineStripeDiscountAmount(line);

  if (stripeAmount > 0) {
    // Two decimal places because the blended rate carries them -- 8.76%, not
    // 9%. Rounding it to a whole number would print a rate the customer was
    // never charged, which is the class of defect this function exists to end.
    const pct =
      grossCents > 0
        ? Math.round((stripeAmount / grossCents) * 10000) / 100
        : 0;
    return { amount: stripeAmount, pct, source: "stripe", metadataAmount, metadataPct };
  }

  if (metadataAmount > 0) {
    return {
      amount: metadataAmount,
      pct: metadataPct,
      source: "metadata",
      metadataAmount,
      metadataPct,
    };
  }

  return { amount: 0, pct: 0, source: "none", metadataAmount, metadataPct };
}
